/**
 * 接続の許可の画面（/mcp/authorize・pages/McpAuthorize.tsx）で、最初に選んでおくプロジェクトと、ボタンの上の 1 行（対象: …）の決まり（純関数）。
 * consent-one-button（2026-10-14 founder「ALLOW ボタン押す前に、閉じてる詳細を開いてプロジェクトを選択しなきゃいけないのは不親切。ボタン押すだけにしたい」）。
 *
 * WHY: 以前の画面は、検証用（staging）では「選んだものだけ」で何も選ばれていない形から始まり、プロジェクトの欄は閉じた「詳細」の中にあった
 *   = Allow を押すと「Choose at least one project, or allow all projects.」で断られ、詳細を開いて選び直す必要があった。
 *   ここで「そのまま押して困らない」初めの状態を決め、何が選ばれているかをボタンの上に 1 行で見せる（変えたい人だけ詳細を開く）。
 *
 * 決まり:
 *   - 追加の許可（今の接続がある = request.existing）: 今の接続が触れるプロジェクトをそのまま選ぶ（**広げない**）。
 *       null（すべて）→ すべて。配列 → そのうち一覧にあるもの（消したプロジェクト・検証用の許可リストに無いものは落ちる）。
 *       一覧に 1 つも残らない・古い本体で projectIds が無い = 分からない → 「すべて」には倒さない（下の初めての決まりから「すべて」を除いたもの）
 *   - 初めての接続: プロジェクトが 1 つならそれ。0 個・2 つ以上なら「すべてのプロジェクト」（後から作るものも）。
 *   - 検証用（staging）: 「すべて」は選べない（本体が 403・画面も出さない）。一覧は本体が KUMO_MCP_STAGING_PROJECT_IDS で絞ったもの
 *       （mcpAuthzDeveloper.ts ③）= 一覧の全部を選んでおく（追加の許可では今の接続のものだけ）。
 *       開いた形（request.stagingOpen・2026-10-15 core/staging-open）は絞らない = 呼ぶ側が staging=false で渡す（ふつうの MCP と同じ初めの状態）。
 * これは画面の初めの状態だけ。送る前の確かめ（mcpAuthorize.ts approvalBody: 何も無ければ断る・自分のものでない ID は捨てる）と
 * 本体の確かめ（④ approve: 本人のもの・検証用の許可リスト）は変えない。
 * import は mcpAuthorize.ts の型だけ（node --test でそのまま動く = mcpAuthorize.ts と同じ形）
 */

import type { ProjectsMode } from './mcpAuthorize.ts';

export interface ProjectsStart {
  mode: ProjectsMode;
  picked: string[];
}

/** 本体の request.existing の形（projectIds は 2026-10-14 から。無い = 古い本体 = 分からない） */
export type ExistingGrant = { scopes?: readonly string[]; projectIds?: readonly string[] | null } | null | undefined;

export function initialProjects(existing: ExistingGrant, projects: ReadonlyArray<{ id: string }>, staging: boolean): ProjectsStart {
  const listed = projects.map((p) => p.id);
  const firstTime = (allowAll: boolean): ProjectsStart => {
    if (staging) return { mode: 'selected', picked: listed };
    if (listed.length === 1) return { mode: 'selected', picked: listed };
    return allowAll ? { mode: 'all', picked: [] } : { mode: 'selected', picked: [] };
  };
  const hasGrant = Array.isArray(existing?.scopes) && existing.scopes.length > 0;
  if (!hasGrant) return firstTime(true);
  const ids = existing?.projectIds;
  if (ids === null) return staging ? { mode: 'selected', picked: listed } : { mode: 'all', picked: [] };
  if (Array.isArray(ids)) {
    const had = new Set(ids);
    const keep = listed.filter((id) => had.has(id));
    if (keep.length > 0) return { mode: 'selected', picked: keep };
  }
  // 今の接続のプロジェクトが分からない・もう無い: 「すべて」に広げない
  return firstTime(false);
}

/** ボタンの上の 1 行に出す名前の数（それより多ければ「ほか N 件」）。スマホ幅で 1〜2 行に収まる数 */
export const SUMMARY_NAMES_MAX = 2;

export type ProjectsSummary = { kind: 'all' } | { kind: 'none' } | { kind: 'some'; names: string[]; more: number };

/** 今選ばれているプロジェクト（画面の状態から）。一覧の順・一覧に無い ID は数えない（送るときに approvalBody も捨てる） */
export function projectsSummary(mode: ProjectsMode, picked: ReadonlySet<string> | readonly string[], projects: ReadonlyArray<{ id: string; name: string }>): ProjectsSummary {
  if (mode === 'all') return { kind: 'all' };
  const set = new Set(picked);
  const names = projects.filter((p) => set.has(p.id)).map((p) => p.name);
  if (names.length === 0) return { kind: 'none' };
  return { kind: 'some', names: names.slice(0, SUMMARY_NAMES_MAX), more: Math.max(0, names.length - SUMMARY_NAMES_MAX) };
}
